import pdfMake from "pdfmake/build/pdfmake"
import { generateDepartmentPerformanceAgreementPDFNew } from "../../functions/scorecard-pdf/GeneratePerformanceAgreementPDF"
import { IMeasureDepartment } from "../../models/MeasureDepartment"
import { IObjective } from "../../models/Objective"

export interface EmailScorecardData {
  title: string
  vision: string
  mission: string
  strategicObjectives: IObjective[]
  contributoryObjectives: IObjective[]
  measures: IMeasureDepartment[]
  recipients: string[]
  message?: string
  senderEmail?: string
}

export class EmailScorecardService {
  private static readonly CLOUD_FUNCTION_URL =
    "https://sendemailsv2-wlg4vkscva-uc.a.run.app"

  static async shareScorecard(data: EmailScorecardData): Promise<void> {
    try {
      // Generate PDF content
      const pdfContent = await this.generatePDFContent(data)

      // Create HTML content for email
      const htmlContent = this.createEmailHTML(data)

      // Send email via Firebase Cloud Function
      await this.sendEmail({
        pdfContent,
        htmlContent,
        to: data.recipients,
        from: data.senderEmail || "noreply@yourcompany.com",
        subject: `Scorecard: ${data.title}`,
        filename: `${data.title}-Scorecard.pdf`,
      })
    } catch (error) {
      console.error("Error sharing scorecard:", error)
      throw new Error("Failed to share scorecard via email")
    }
  }

  private static async generatePDFContent(data: EmailScorecardData): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        // Get the document definition
        const docDefinition = await generateDepartmentPerformanceAgreementPDFNew(
          data.title,
          data.vision,
          data.mission,
          data.strategicObjectives,
          data.contributoryObjectives,
          data.measures,
        )

        // Create PDF and get base64 content
        const pdfDoc = pdfMake.createPdf(docDefinition as any)

        pdfDoc.getBase64((base64Content) => {
          resolve(base64Content)
        })
      } catch (error) {
        reject(error)
      }
    })
  }

  private static createEmailHTML(data: EmailScorecardData): string {
    const currentDate = new Date().toLocaleDateString()

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Scorecard Shared</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
          }
          .header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 20px;
            border-radius: 8px;
            text-align: center;
            margin-bottom: 20px;
          }
          .content {
            background: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 20px;
          }
          .footer {
            text-align: center;
            color: #666;
            font-size: 12px;
            border-top: 1px solid #eee;
            padding-top: 20px;
          }
          .attachment-info {
            background: #e3f2fd;
            border-left: 4px solid #2196f3;
            padding: 15px;
            margin: 15px 0;
          }
          .message-box {
            background: white;
            border: 1px solid #ddd;
            border-radius: 4px;
            padding: 15px;
            margin: 15px 0;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>📊 Scorecard Shared</h1>
          <p>Performance Management System</p>
        </div>

        <div class="content">
          <h2>Hello,</h2>
          <p>You have received a scorecard document: <strong>${data.title}</strong></p>
          
          ${
            data.message
              ? `
          <div class="message-box">
            <h3>Personal Message:</h3>
            <p>${data.message.replace(/\n/g, "<br>")}</p>
          </div>
          `
              : ""
          }

          <div class="attachment-info">
            <h3>📎 Attachment Details:</h3>
            <ul>
              <li><strong>Document:</strong> ${data.title} Scorecard</li>
              <li><strong>Format:</strong> PDF</li>
              <li><strong>Generated:</strong> ${currentDate}</li>
            </ul>
          </div>

          <p>Please find the detailed scorecard attached to this email. The document contains:</p>
          <ul>
            <li>Strategic objectives and measures</li>
            <li>Performance targets and ratings</li>
            <li>Key initiatives and timelines</li>
            <li>Progress tracking information</li>
          </ul>

          <p>If you have any questions about this scorecard, please don't hesitate to reach out.</p>
        </div>

        <div class="footer">
          <p>This email was sent from the Performance Management System</p>
          <p>Generated on ${currentDate}</p>
        </div>
      </body>
      </html>
    `
  }

  private static async sendEmail(emailData: {
    pdfContent: string
    htmlContent: string
    to: string[]
    from: string
    subject: string
    filename: string
  }): Promise<void> {
    const response = await fetch(this.CLOUD_FUNCTION_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        pdfContent: emailData.pdfContent,
        htmlContent: emailData.htmlContent,
        to: emailData.to,
        from: emailData.from,
        subject: emailData.subject,
        filename: emailData.filename,
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to send email: ${errorText}`)
    }
  }
}
